import {useQuery} from '@tanstack/react-query';
import {getReportJobDevices, listReportJobs} from 'src/services/reportService';

export interface ReportJob {
  id: number;
  userId: number;
  customerId: number;
  reportType: string;
  format: string;
  parameters: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled' | 'expired';
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  expiresAt?: string;
  viewedAt?: string;
  estimatedSeconds: number;
  failure?: string;
  cancelledBy: number;
  deviceCount: number;
  requesterEmail?: string;
}

export interface ReportJobDeviceUsage {
  imei?: string;
  name?: string;
  model?: string;
  plate?: string;
  customerName?: string;
  distance: number;
  spentFuel: number;
}

export interface ReportJobPage {
  items: ReportJob[];
  total: number;
  /** Página servida, base zero. Com `jobId` é a que contém o pedido; sem ele (ou se o pedido não é visível), a pedida. */
  page: number;
}

export const reportJobsKeys = {
  all: ['reportJobs'] as const,
  list: (page: number, pageSize: number) => ['reportJobs', 'list', page, pageSize] as const,
  /** Fora de `all`: a invalidação por socket não alcança a consulta do link. */
  lookup: (jobId: number, pageSize: number, click: string | null) =>
    ['reportJobLookup', jobId, pageSize, click] as const,
  devices: (jobId: number) => ['reportJobs', jobId, 'devices'] as const,
};

/** Intervalo de reconsulta enquanto há pedido na fila ou em geração. */
export const ACTIVE_JOBS_REFETCH_MS = 10000;

/**
 * Reconsulta a lista de tempos em tempos enquanto houver pedido na fila ou em geração. O socket avisa
 * quando o pedido termina, mas ele pode estar fora (a tela espera 60 s para tentar de novo, e nada
 * reconsulta ao reconectar); a consulta periódica garante que o pedido pronto apareça mesmo assim.
 * Sem pedido ativo não consulta nada.
 *
 * @param {ReportJobPage=} page página atual da lista
 * @return {number|false} intervalo em milissegundos, ou `false` para não reconsultar
 */
export const refetchIntervalWhileActive = (page?: ReportJobPage) =>
  page?.items.some((job) => job.status === 'pending' || job.status === 'running') ?
    ACTIVE_JOBS_REFETCH_MS : false;

export const useReportJobs = (page: number, pageSize: number) => {
  return useQuery<unknown, unknown, ReportJobPage>({
    queryKey: reportJobsKeys.list(page, pageSize),
    queryFn: () => listReportJobs(page, pageSize),
    // Ao trocar de página, a anterior fica na tela até a próxima chegar; sem dado nenhum só na primeira carga.
    keepPreviousData: true,
    refetchInterval: (data) => refetchIntervalWhileActive(data as ReportJobPage | undefined),
  });
};

/**
 * Descobre em que página está o pedido do link da notificação: pede ao servidor com `jobId` e o
 * `page` da resposta é a página que a tela deve abrir. É a única consulta que leva `jobId`; a lista
 * (`useReportJobs`) segue sempre a página que o usuário está olhando, então reconsulta por socket ou
 * por intervalo nunca a leva de volta ao pedido.
 *
 * PERFORMANCE: a chave leva o valor do clique na notificação e `cacheTime: 0`, para um mesmo link aberto
 * de novo perguntar de novo em vez de usar uma página guardada que já pode ter mudado. `enabled` é
 * `false` depois que a tela registrou o desfecho (página adotada ou falha). A chave fica fora de
 * `reportJobsKeys.all` e a reconexão não reconsulta: nem socket nem rede puxam a página de quem já
 * paginou.
 *
 * @param {number|null} jobId pedido do link, ou `null` para não consultar
 * @param {number} pageSize tamanho de página atual (o servidor calcula a página com ele)
 * @param {string|null} click valor do clique na notificação que trouxe o link (`helpers/notificationClick`)
 * @param {boolean} enabled `false` quando a resposta já foi adotada
 * @return {Object} resultado da consulta; `data.page` é a página que contém o pedido
 */
export const useReportJobLookup = (
  jobId: number | null, pageSize: number, click: string | null, enabled: boolean,
) => {
  return useQuery<unknown, unknown, ReportJobPage>({
    queryKey: reportJobsKeys.lookup(jobId!, pageSize, click),
    queryFn: () => listReportJobs(0, pageSize, jobId!),
    enabled: enabled && jobId !== null,
    cacheTime: 0,
    refetchOnReconnect: false,
    // Uma nova tentativa basta: as três do padrão seguram o recuo da página vazia e a adoção por segundos com a rede fora.
    retry: 1,
  });
};

export const useReportJobDevices = (jobId: number | null) => {
  return useQuery<unknown, unknown, ReportJobDeviceUsage[]>({
    // enabled trava a query com jobId nulo -- dentro de queryFn ele já é sempre um número real.
    queryKey: reportJobsKeys.devices(jobId!),
    queryFn: () => getReportJobDevices(jobId!),
    enabled: jobId !== null,
    initialData: [],
    // Uma nova tentativa basta: as três do padrão seguram o modal em "Carregando..." por segundos antes de o erro aparecer.
    retry: 1,
  });
};
